import { Action, createReducer, on } from '@ngrx/store';
import { createEntityAdapter, EntityAdapter, EntityState, Update } from '@ngrx/entity';

import * as interventionActions from './intervention.actions';
import { Intervention } from 'src/app/models/intervention';
import { sortByDate } from '../../studies/helpers/date-helper';

export interface InterventionState extends EntityState<Intervention> {
  loadingIntervention: boolean;
  interventionLoaded: boolean;
  loadingInterventions: boolean;
  interventionsLoaded: boolean;
  selectedIntervention: Intervention;
  startModuleSet: boolean;
}

export function sortByCreateDate(intervention1: Intervention, intervention2: Intervention): number {
  return sortByDate(intervention1?.createdAt, intervention2?.createdAt);
}

export const interventionAdapter: EntityAdapter<Intervention> = createEntityAdapter<Intervention>({
  sortComparer: sortByCreateDate,
});

export const initialInterventionState: InterventionState = interventionAdapter.getInitialState({
  loadingIntervention: true,
  interventionLoaded: false,
  loadingInterventions: true,
  interventionsLoaded: false,
  selectedIntervention: null,
  startModuleSet: false,
});

export const reducer = createReducer(
  initialInterventionState,
  on(interventionActions.getInterventionsSuccess, (state, { interventions }) => {
    return interventionAdapter.setAll(interventions, {
      ...state,
      loadingInterventions: false,
      interventionsLoaded: true,
      selectedIntervention: null,
    });
  }),
  on(interventionActions.selectIntervention, (state) => {
    return {
      ...state,
    };
  }),
  on(interventionActions.selectInterventionSuccess, (state, { selectedIntervention }) => {
    return {
      ...state,
      selectedIntervention,
    };
  }),
  on(interventionActions.setSelectedIntervention, (state, { selectedIntervention }) => {
    return {
      ...state,
      selectedIntervention,
    };
  }),
  on(interventionActions.saveInterventionSuccess, (state, { newIntervention }) => {
    return interventionAdapter.addOne(newIntervention, {
      ...state,
      loadingSelectedIntervention: false,
      selectedInterventionLoaded: true,
      selectedIntervention: newIntervention,
    });
  }),
  on(interventionActions.updateInterventionSuccess, (state, { updatedIntervention }) => {
    return interventionAdapter.updateOne(
      { id: updatedIntervention.id, changes: updatedIntervention },
      {
        ...state,
        loadingSelectedIntervention: false,
        selectedInterventionLoaded: true,
        selectedIntervention: updatedIntervention,
      }
    );
  }),
  on(interventionActions.deleteInterventionSuccess, (state, { deletedIntervention }) => {
    return interventionAdapter.removeOne(deletedIntervention.id, {
      ...state,
      selectedIntervention: null,
    });
  }),
  on(interventionActions.duplicateInterventionSuccess, (state, { duplicatedIntervention }) => {
    return interventionAdapter.addOne(duplicatedIntervention, {
      ...state,
    });
  }),
  on(interventionActions.getIntervention, (state) => {
    return {
      ...state,
      loadingIntervention: true,
      interventionLoaded: false,
    };
  }),
  on(interventionActions.getInterventionSuccess, (state, { intervention }) => {
    return interventionAdapter.updateOne(
      { id: intervention.id, changes: intervention },
      {
        ...state,
        loadingIntervention: false,
        interventionLoaded: true,
        selectedIntervention: intervention,
      }
    );
  }),
  on(interventionActions.getInterventionError, (state) => {
    return {
      ...state,
      loadingIntervention: false,
      interventionLoaded: false,
    };
  }),
  on(interventionActions.setStartModuleSuccess, (state) => {
    return {
      ...state,
      startModuleSet: true,
    };
  }),
  on(interventionActions.resetStartModuleSetFlag, (state) => {
    return {
      ...state,
      startModuleSet: false,
    };
  }),
  on(interventionActions.updateNonDefaultInterventions, (state, { nonDefaultInterventions }) => {
    const interventionUpdates: Update<Intervention>[] = [];
    nonDefaultInterventions.map((intervention) =>
      interventionUpdates.push({
        id: intervention.id,
        changes: {
          ...intervention,
          isDefault: 0,
        },
      })
    );
    return interventionAdapter.updateMany(interventionUpdates, {
      ...state,
    });
  })
);

export function interventionReducer(state: InterventionState | undefined, action: Action) {
  return reducer(state, action);
}

export const { selectIds, selectEntities, selectAll, selectTotal } = interventionAdapter.getSelectors();
